(() => {
  if (window.EvayongIntroGateways) { window.EvayongIntroGateways.scan(document); return; }
  const gateways = new WeakMap();
  const scripts = new Map();

  class IntroGateway {
    constructor(element) {
      this.element = element;
      this.stage = element.querySelector('.ey-stage');
      this.hit = element.querySelector('.ey-interaction');
      this.loader = element.querySelector('.ey-loader');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.controller = new AbortController();
      this.captionTimers = new Set();
      this.lightweight = false;
      this.previousTop = this.stage.getBoundingClientRect().top;
      const options = { signal: this.controller.signal };
      document.addEventListener('evayong:navigation-opened', () => {
        cancelAnimationFrame(this.navigationScrollFrame);
        this.navigationScrollSettling = true;
        this.resetScrollIntent();
      }, options);
      document.addEventListener('evayong:navigation-closed', () => {
        cancelAnimationFrame(this.navigationScrollFrame);
        this.navigationScrollSettling = true;
        this.resetScrollIntent();
        // Ignore layout movement while the menu restores the page's scroll position.
        this.navigationScrollFrame = requestAnimationFrame(() => {
          this.resetScrollIntent();
          this.navigationScrollSettling = false;
        });
      }, options);
      document.addEventListener('scroll', () => this.checkScroll(), { ...options, passive: true, capture: true });
      this.stage.addEventListener('wheel', event => this.scrollIntent(event.deltaY), { ...options, passive:true });
      this.stage.addEventListener('touchstart', event => {
        this.touch = event.touches.length === 1 ? { x:event.touches[0].clientX, y:event.touches[0].clientY } : null;
      }, { ...options, passive:true });
      this.stage.addEventListener('touchmove', event => {
        if (!this.touch || event.touches.length !== 1) return;
        const point = event.touches[0]; const dx = point.clientX-this.touch.x; const dy = this.touch.y-point.clientY;
        if (Math.abs(dy)>Math.abs(dx)) this.scrollIntent(dy);
        this.touch = {x:point.clientX,y:point.clientY};
      }, { ...options, passive:true });
      element.addEventListener('click', event => {
        if (event.target.closest('[data-ey-return]') && this.lightweight) {
          event.preventDefault(); this.close();
        } else if (event.target.closest('.ey-interaction') && (this.lightweight || element.dataset.renderer !== 'ready')) {
          event.preventDefault(); this.requestOpen();
        }
      }, options);
      this.hit.addEventListener('keydown', event => {
        if ((this.lightweight || element.dataset.renderer !== 'ready') && ['Enter',' '].includes(event.key)) {
          event.preventDefault(); this.requestOpen();
        }
      }, options);
      element.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.lightweight && element.dataset.state === 'open' && !document.querySelector('.ey-signup[open]')) this.close();
      }, options);
      element.addEventListener('evayong:graphics-failed', event => {
        this.useLightweight(event.detail?.reason || 'graphics', element.dataset.state !== 'closed');
      }, options);
      element.addEventListener('evayong:graphics-ready', () => {
        this.hideLoader();
        if (this.pendingOpen) {
          const request = this.pendingOpen;
          this.pendingOpen = null;
          element.open(request);
        }
      }, options);
      document.addEventListener('evayong:return-to-box', () => { if (this.lightweight) this.close(); }, options);
    }

    start() {
      const element = this.element;
      if (element.dataset.animationMode === 'lightweight') {
        this.useLightweight('setting', false);
        this.checkScroll();
        return;
      }
      element.dataset.renderer = 'pending';
      this.scheduleLoader();
      this.checkScroll();
      if (this.lightweight) return;
      this.loadTimer = setTimeout(() => {
        if (this.lightweight || !element.isConnected) return;
        this.engineAllowed = true;
        if (customElements.get('evayong-intro')) { element.connectedCallback(); return; }
        const url = element.dataset.engine;
        if (!scripts.has(url)) {
          scripts.set(url, new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url; script.async = true;
            script.onload = resolve; script.onerror = reject;
            document.head.append(script);
          }));
        }
        scripts.get(url).catch(() => this.useLightweight('script-error', Boolean(this.pendingOpen)));
      }, 80);
    }

    scheduleLoader() {
      this.hideLoader();
      this.loaderTimer = setTimeout(() => {
        this.loaderTimer = null;
        if (!this.element.isConnected || this.lightweight || this.element.dataset.renderer !== 'pending') return;
        this.element.dataset.assetLoading = 'true';
        this.loader?.setAttribute('aria-hidden', 'false');
        const status = this.loader?.querySelector('[data-ey-loading-status]');
        if (status) status.textContent = this.loader.dataset.loadingLabel;
      }, 350);
    }

    hideLoader() {
      clearTimeout(this.loaderTimer);
      this.loaderTimer = null;
      delete this.element.dataset.assetLoading;
      this.loader?.setAttribute('aria-hidden', 'true');
      const status = this.loader?.querySelector('[data-ey-loading-status]');
      if (status) status.textContent = '';
    }

    requestOpen(options = {}) {
      if (this.lightweight) this.open();
      else if (this.element.dataset.renderer === 'ready') this.element.open(options);
      else this.pendingOpen = options;
    }

    checkScroll() {
      const rect = this.stage.getBoundingClientRect();
      const delta = this.previousTop-rect.top;
      this.previousTop = rect.top;
      if (rect.bottom <= 0 || rect.top >= innerHeight) return;
      this.scrollIntent(delta);
    }

    resetScrollIntent() {
      this.previousTop = this.stage.getBoundingClientRect().top;
      this.scrollDistance = 0;
      this.scrollDirection = 0;
      this.touch = null;
    }

    scrollIntent(delta) {
      if (!Number.isFinite(delta) || Math.abs(delta)<1 || this.navigationScrollSettling || document.querySelector('.ey-signup[open], #EYNavigationDialog[open]')) return;
      const direction = Math.sign(delta);
      if (direction !== this.scrollDirection) this.scrollDistance = 0;
      this.scrollDirection = direction;
      this.scrollDistance = (this.scrollDistance || 0) + Math.abs(delta);
      if (this.scrollDistance < 20) return;
      this.scrollDistance = 0;
      if (direction > 0) {
        this.requestOpen({focus:false});
      } else {
        this.pendingOpen = null;
        this.element.requestedOpen = false;
        if (this.lightweight) this.close({scrollToStage:false,focus:false});
        else if (this.element.dataset.renderer === 'ready' && this.element.dataset.state !== 'closed') this.element.returnToBox({scrollToStage:false,focus:false});
      }
    }

    useLightweight(reason, reveal) {
      this.hideLoader();
      clearTimeout(this.loadTimer);
      reveal = reveal || Boolean(this.pendingOpen);
      this.pendingOpen = null;
      if (!this.lightweight) {
        this.lightweight = true;
        this.element.dataset.lite = 'true';
        if (this.element.controller) this.element.disconnectedCallback?.();
        this.element.dataset.renderer = 'static';
        this.element.dataset.fallbackReason = reason;
        this.hit.setAttribute('aria-label', 'Open the EVAYONG box');
      }
      if (reveal) this.open(true);
      else if (this.element.dataset.state !== 'open') this.element.dataset.state = 'closed';
    }

    open(force = false) {
      const element = this.element;
      if (element.dataset.state === 'open' && !force) return;
      clearTimeout(this.closeTimer);
      element.dataset.state = 'open';
      this.hit.setAttribute('aria-expanded','true');
      this.hit.setAttribute('tabindex','-1');
      const heart = element.querySelector('.ey-emblem');
      heart.disabled = false; heart.setAttribute('aria-hidden','false'); heart.setAttribute('tabindex','0');
      this.showCaption();
      element.querySelector('.ey-status').textContent = 'The box is open. Continue below or visit Shop.';
    }

    close({scrollToStage=true,focus=true} = {}) {
      const element = this.element;
      if (element.dataset.state === 'closed' || element.dataset.state === 'closing') return;
      this.clearCaption();
      if(scrollToStage)this.stage.scrollIntoView({ behavior:'instant', block:'start' });
      element.dataset.state = 'closing';
      const heart = element.querySelector('.ey-emblem');
      heart.setAttribute('aria-hidden','true'); heart.setAttribute('tabindex','-1');
      const cta = element.querySelector('.ey-reveal-signup');
      cta.setAttribute('aria-hidden','true'); cta.setAttribute('tabindex','-1');
      this.closeTimer = setTimeout(() => {
        element.dataset.state = 'closed'; element.dataset.caption = 'logo';
        this.hit.setAttribute('aria-expanded','false'); this.hit.setAttribute('tabindex','0');
        if(focus)this.hit.focus({ preventScroll:true });
        element.querySelector('.ey-status').textContent = 'The box is closed.';
      }, this.motion.matches ? 0 : 420);
    }

    showCaption() {
      this.clearCaption();
      const element = this.element;
      const cta = element.querySelector('.ey-reveal-signup');
      const mark = element.querySelector('.ey-lace-wordmark');
      element.dataset.caption = 'logo';
      mark.setAttribute('aria-hidden','false');
      cta.setAttribute('aria-hidden','true'); cta.setAttribute('tabindex','-1');
      const show = () => {
        if (element.dataset.state !== 'open') return;
        element.dataset.caption = 'signup';
        mark.setAttribute('aria-hidden','true');
        cta.setAttribute('aria-hidden','false'); cta.setAttribute('tabindex','0');
      };
      const hideTimer = setTimeout(() => {
        this.captionTimers.delete(hideTimer);
        if (element.dataset.state !== 'open') return;
        element.dataset.caption = 'fading'; mark.setAttribute('aria-hidden','true');
        const showTimer = setTimeout(() => { this.captionTimers.delete(showTimer); show(); }, this.lightweight && this.motion.matches ? 0 : 360);
        this.captionTimers.add(showTimer);
      }, 1000);
      this.captionTimers.add(hideTimer);
    }

    clearCaption() { for (const timer of this.captionTimers) clearTimeout(timer); this.captionTimers.clear(); }
    dispose() {
      this.hideLoader();
      this.clearCaption(); clearTimeout(this.loadTimer); clearTimeout(this.closeTimer);
      cancelAnimationFrame(this.navigationScrollFrame);
      this.controller.abort(); gateways.delete(this.element);
    }
  }

  function elements(root) {
    const result = [...root.querySelectorAll('evayong-intro')];
    if (root.matches?.('evayong-intro')) result.unshift(root);
    return result;
  }
  function scan(root) {
    for (const element of elements(root)) {
      if (gateways.has(element)) continue;
      const gateway = new IntroGateway(element); gateways.set(element,gateway); gateway.start();
    }
  }
  window.EvayongIntroGateways = {
    scan,
    canInitialize(element) { const gateway = gateways.get(element); return Boolean(gateway && gateway.engineAllowed && !gateway.lightweight); },
    showCaption(element) { gateways.get(element)?.showCaption(); },
    clearCaption(element) { gateways.get(element)?.clearCaption(); }
  };
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const element of elements(event.target)) gateways.get(element)?.dispose();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once:true });
  else scan(document);
})();
